import { getApp, getApps, initializeApp } from 'firebase/app'
import { getAuth, setPersistence, browserLocalPersistence } from 'firebase/auth'
import { getDatabase, ref as dbRef } from 'firebase/database'
import {
  getDownloadURL,
  getStorage,
  ref,
  uploadBytes,
  uploadBytesResumable,
} from 'firebase/storage'

import { OpenAPI } from '@/api/client/requests'

const databaseURL = import.meta.env.VITE_FIREBASE_DATABASE_URL

const app =
  getApps().length > 0
    ? getApp()
    : initializeApp({
        apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
        authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
        databaseURL: import.meta.env.VITE_FIREBASE_DATABASE_URL,
        projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
        storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
        messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
        appId: import.meta.env.VITE_FIREBASE_APP_ID,
      })

export const firebaseAuth = getAuth(app)

if (typeof window !== 'undefined') {
  setPersistence(firebaseAuth, browserLocalPersistence)
}

OpenAPI.TOKEN = async () => {
  const token = await firebaseAuth.currentUser?.getIdToken()

  return token ?? ''
}

export const getStorageDownloadUrl = async (path: string) => {
  const downloadUrl = await getDownloadURL(ref(getStorage(), path))

  return downloadUrl
}

export const uploadToStorage = (path: string, file: File) => {
  return uploadBytes(ref(getStorage(), path), file)
}

// Resumable upload to a Storage path, with optional AbortController support.
// Routes via Firebase's storage protocol (avoids the CORS issues you hit when
// PUTting directly to a v4 signed URL on storage.googleapis.com from a browser).
// AbortSignal is wired to UploadTask.cancel() so the caller can stop the
// transfer mid-flight (e.g. user clicks "X" on a staging-list row).
export const uploadToStoragePath = (filePath: string, file: File, signal?: AbortSignal) => {
  const task = uploadBytesResumable(ref(getStorage(), filePath), file, {
    contentType: file.type || 'application/octet-stream',
  })

  if (signal) {
    if (signal.aborted) {
      task.cancel()
    } else {
      signal.addEventListener('abort', () => task.cancel(), { once: true })
    }
  }

  return new Promise<void>((resolve, reject) => {
    task.on(
      'state_changed',
      undefined,
      err => {
        // 'storage/canceled' fires when task.cancel() ran. Treat as a clean
        // abort, not an error — the caller already knows it cancelled.
        if ((err as { code?: string }).code === 'storage/canceled') {
          const abortErr = new Error('Aborted')
          abortErr.name = 'AbortError'
          reject(abortErr)
          return
        }
        reject(err)
      },
      () => resolve(),
    )
  })
}

export const getRealtimeRef = (path: string) => {
  return dbRef(getDatabase(undefined, databaseURL), path)
}
